import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Package, Send } from "lucide-react";
import { buildWhatsAppLink } from "../lib/constants";

const serviceOptions = [
  { value: "cajas", label: "Envío de Cajas" },
  { value: "tanques", label: "Tanques y Carga Grande" },
  { value: "paquetes", label: "Paquetes Personales" },
  { value: "electrodomesticos", label: "Electrodomésticos" },
  { value: "mudanza", label: "Mudanza Completa" },
  { value: "otro", label: "Otro" },
];

export default function QuoteDialog({ open, onOpenChange }) {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    city: "",
    service: "",
    details: "",
  });

  const handleChange = (key) => (e) => {
    const v = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [key]: v }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const serviceLabel = serviceOptions.find((s) => s.value === form.service)?.label || form.service || "No especificado";
    const message =
      `Hola San José Shipping LLC 👋\n\n` +
      `Quisiera cotizar un envío:\n\n` +
      `• Nombre: ${form.name || "-"}\n` +
      `• Teléfono: ${form.phone || "-"}\n` +
      `• Ciudad destino (RD): ${form.city || "-"}\n` +
      `• Tipo de envío: ${serviceLabel}\n` +
      `• Detalles: ${form.details || "-"}\n\n` +
      `Quedo atento(a) a su respuesta. ¡Gracias!`;
    window.open(buildWhatsAppLink(message), "_blank", "noopener,noreferrer");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="quote-dialog"
        className="sm:max-w-lg p-0 gap-0 border-0 rounded-none overflow-hidden"
      >
        <div className="bg-[#0B1F4D] text-white px-6 py-5 border-b-4 border-[#D61F26]">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 bg-[#D61F26] flex items-center justify-center">
              <Package className="w-5 h-5" strokeWidth={2.5} />
            </div>
            <DialogHeader className="space-y-1 text-left">
              <DialogTitle className="font-display text-xl uppercase tracking-tight text-white">
                Cotiza tu envío
              </DialogTitle>
              <DialogDescription className="text-xs uppercase tracking-wider text-white/70">
                Te responderemos por WhatsApp
              </DialogDescription>
            </DialogHeader>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-6 space-y-4 bg-white">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="q-name" className="text-xs uppercase tracking-wider font-bold text-[#0B1F4D]">Nombre</Label>
              <Input
                id="q-name"
                data-testid="quote-input-name"
                required
                value={form.name}
                onChange={handleChange("name")}
                placeholder="Tu nombre"
                className="rounded-none border-gray-300 focus-visible:ring-[#D61F26]"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="q-phone" className="text-xs uppercase tracking-wider font-bold text-[#0B1F4D]">Teléfono</Label>
              <Input
                id="q-phone"
                data-testid="quote-input-phone"
                required
                value={form.phone}
                onChange={handleChange("phone")}
                placeholder="(123) 456-7890"
                className="rounded-none border-gray-300 focus-visible:ring-[#D61F26]"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="q-city" className="text-xs uppercase tracking-wider font-bold text-[#0B1F4D]">Ciudad destino (RD)</Label>
            <Input
              id="q-city"
              data-testid="quote-input-city"
              value={form.city}
              onChange={handleChange("city")}
              placeholder="Ej. Santo Domingo, Santiago..."
              className="rounded-none border-gray-300 focus-visible:ring-[#D61F26]"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider font-bold text-[#0B1F4D]">Tipo de envío</Label>
            <Select value={form.service} onValueChange={handleChange("service")}>
              <SelectTrigger data-testid="quote-select-service" className="rounded-none border-gray-300 focus:ring-[#D61F26]">
                <SelectValue placeholder="Selecciona un servicio" />
              </SelectTrigger>
              <SelectContent>
                {serviceOptions.map((o) => (
                  <SelectItem key={o.value} value={o.value} data-testid={`quote-service-option-${o.value}`}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="q-details" className="text-xs uppercase tracking-wider font-bold text-[#0B1F4D]">Detalles del envío</Label>
            <Textarea
              id="q-details"
              data-testid="quote-input-details"
              value={form.details}
              onChange={handleChange("details")}
              placeholder="Cuéntanos qué quieres enviar, peso aproximado, cantidad..."
              rows={4}
              className="rounded-none border-gray-300 focus-visible:ring-[#D61F26]"
            />
          </div>

          <button
            type="submit"
            data-testid="quote-submit-btn"
            className="w-full inline-flex items-center justify-center gap-2 bg-[#D61F26] text-white px-7 py-4 uppercase font-bold tracking-wider hover:bg-[#A3161C] transition-all text-sm"
          >
            <Send className="w-4 h-4" strokeWidth={2.5} />
            Enviar por WhatsApp
          </button>
          <p className="text-[11px] text-gray-500 text-center uppercase tracking-wider">
            Al enviar, se abrirá WhatsApp con tu mensaje pre-llenado
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
